/**
 * INTENSE 101 - Premium Luxury Shopify Header Logic
 * Vanilla JavaScript implementation for high performance & Lighthouse optimization.
 */

document.addEventListener('DOMContentLoaded', () => {
  initStickyHeader();
  initHeroBrightnessDetector();
  initDrawers();
  initPredictiveSearch();
  initCartDrawer();
  initMobileMenu();
  initAccessibility();
  initLogoBackgroundRemoval();
});

/**
 * Sticky Header Interaction
 */
function initStickyHeader() {
  const headerWrapper = document.querySelector('.intense-header-wrapper');
  if (!headerWrapper) return;
  const announcementBar = document.getElementById('announcement-bar');

  const scrollThreshold = 30; // Threshold of 30px

  const handleScroll = () => {
    if (window.scrollY > scrollThreshold) {
      headerWrapper.classList.add('is-scrolled', 'scrolled');
      if (announcementBar) announcementBar.classList.add('is-scrolled', 'scrolled');
    } else {
      headerWrapper.classList.remove('is-scrolled', 'scrolled');
      if (announcementBar) announcementBar.classList.remove('is-scrolled', 'scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Run initially in case page is refreshed while scrolled
}

/**
 * Dynamic Hero Image Brightness Detector
 * Adjusts background glass opacity dynamically:
 * - Bright slides: increases opacity to 0.18 for premium legibility.
 * - Dark slides: decreases opacity to 0.08 for high-contrast blend.
 */
function initHeroBrightnessDetector() {
  const slideshow = document.querySelector('.bls__slideshow');
  const headerWrapper = document.querySelector('.intense-header-wrapper');
  if (!slideshow || !headerWrapper) return;

  const updateOpacity = (imgEl) => {
    if (!imgEl) {
      headerWrapper.style.setProperty('--header-bg-glass-opacity', '0.10');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 10;
    canvas.height = 10;
    const ctx = canvas.getContext('2d');

    const analyzeImage = (src) => {
      const tempImg = new Image();
      tempImg.crossOrigin = "Anonymous";
      tempImg.src = src;
      tempImg.onload = function() {
        try {
          ctx.drawImage(tempImg, 0, 0, 10, 10);
          const imageData = ctx.getImageData(0, 0, 10, 10);
          const data = imageData.data;
          let r, g, b, avg;
          let colorSum = 0;
          for (let i = 0; i < data.length; i += 4) {
            r = data[i];
            g = data[i+1];
            b = data[i+2];
            // Standard relative luminance formula
            avg = 0.299 * r + 0.587 * g + 0.114 * b;
            colorSum += avg;
          }
          const finalBrightness = colorSum / 100;
          
          if (finalBrightness > 150) {
            headerWrapper.style.setProperty('--header-bg-glass-opacity', '0.18');
            headerWrapper.classList.add('slide-is-light');
            headerWrapper.classList.remove('slide-is-dark');
          } else {
            headerWrapper.style.setProperty('--header-bg-glass-opacity', '0.08');
            headerWrapper.classList.add('slide-is-dark');
            headerWrapper.classList.remove('slide-is-light');
          }
        } catch(e) {
          headerWrapper.style.setProperty('--header-bg-glass-opacity', '0.10');
        }
      };
      tempImg.onerror = function() {
        headerWrapper.style.setProperty('--header-bg-glass-opacity', '0.10');
      };
    };

    analyzeImage(imgEl.currentSrc || imgEl.src);
  };

  const checkActiveSlide = () => {
    const activeSlide = slideshow.querySelector('.swiper-slide-active');
    if (activeSlide) {
      const img = activeSlide.querySelector('img');
      if (img) {
        if (img.complete) {
          updateOpacity(img);
        } else {
          img.addEventListener('load', () => updateOpacity(img), { once: true });
        }
      }
    }
  };

  // MutationObserver detects active slide change in Swiper slides
  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;
    for (const mutation of mutations) {
      if (mutation.attributeName === 'class') {
        shouldCheck = true;
        break;
      }
    }
    if (shouldCheck) {
      checkActiveSlide();
    }
  });

  const slides = slideshow.querySelectorAll('.swiper-slide');
  slides.forEach(slide => {
    observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
  });

  checkActiveSlide();
}

/**
 * Common Drawer Toggling Utility
 */
let activeDrawer = null;

function initDrawers() {
  const overlay = document.querySelector('.intense-drawer-overlay');
  const closeBtns = document.querySelectorAll('.js-drawer-close');
  
  if (overlay) {
    overlay.addEventListener('click', () => {
      closeAllDrawers();
    });
  }

  closeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      closeAllDrawers();
    });
  });

  // Handle trigger buttons
  document.querySelectorAll('[data-drawer-trigger]').forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = trigger.getAttribute('data-drawer-trigger');
      openDrawer(targetId);
    });
  });
}

function openDrawer(drawerId) {
  closeAllDrawers();
  
  const drawer = document.getElementById(drawerId);
  const overlay = document.querySelector('.intense-drawer-overlay');
  
  if (drawer) {
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden'; // Prevents background scrolling
    activeDrawer = drawer;
    
    // Auto-focus first input or close button
    setTimeout(() => {
      const focusable = drawer.querySelector('input, button');
      if (focusable) focusable.focus();
    }, 100);

    // If opening cart drawer, refresh cart content
    if (drawerId === 'intense-cart-drawer') {
      document.body.classList.add('intense-cart-open');
      refreshCartContent();
    }
  }
}

function closeAllDrawers() {
  document.querySelectorAll('.intense-drawer, .intense-search-drawer').forEach(drawer => {
    drawer.classList.remove('is-open');
  });

  const overlay = document.querySelector('.intense-drawer-overlay');
  if (overlay) overlay.classList.remove('is-visible');
  
  document.body.style.overflow = '';
  document.body.classList.remove('mobile-menu-open');
  document.body.classList.remove('intense-cart-open');
  activeDrawer = null;
}

/**
 * Predictive Search Feature
 */
function initPredictiveSearch() {
  const searchInput = document.querySelector('.js-intense-search-input');
  const resultsContainer = document.querySelector('.js-intense-search-results');
  const loader = document.querySelector('.js-intense-search-loader');
  const trendingContainer = document.querySelector('.js-intense-search-trending');
  const recentContainer = document.querySelector('.js-intense-search-recent');
  
  if (!searchInput) return;

  // Render Recent Searches from LocalStorage
  renderRecentSearches();

  let debounceTimeout = null;

  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim();
    
    if (debounceTimeout) clearTimeout(debounceTimeout);

    if (query.length === 0) {
      loader.style.display = 'none';
      if (resultsContainer) resultsContainer.innerHTML = '';
      if (trendingContainer) trendingContainer.style.display = '';
      if (recentContainer) recentContainer.style.display = '';
      return;
    }

    if (trendingContainer) trendingContainer.style.display = 'none';
    if (recentContainer) recentContainer.style.display = 'none';
    loader.style.display = 'block';

    debounceTimeout = setTimeout(() => {
      fetchPredictiveSearchResults(query);
    }, 300);
  });

  // Handle trending click
  document.querySelectorAll('.js-trending-search-item').forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const term = item.getAttribute('data-search-term');
      searchInput.value = term;
      searchInput.dispatchEvent(new Event('input'));
    });
  });

  // Handle Form Submission - save search term
  const searchForm = document.querySelector('.intense-search-form');
  if (searchForm) {
    searchForm.addEventListener('submit', () => {
      const query = searchInput.value.trim();
      if (query.length > 0) {
        saveRecentSearch(query);
      }
    });
  }
}

function fetchPredictiveSearchResults(query) {
  const resultsContainer = document.querySelector('.js-intense-search-results');
  const loader = document.querySelector('.js-intense-search-loader');

  // Shopify suggest endpoint
  const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;

  fetch(url)
    .then(response => response.json())
    .then(data => {
      loader.style.display = 'none';
      if (!resultsContainer) return;

      const products = data.resources.results.products;
      
      if (!products || products.length === 0) {
        resultsContainer.innerHTML = `<div class="intense-search-suggestions__no-results">No fragrances found for "${escapeHtml(query)}".</div>`;
        return;
      }

      let html = '<div class="intense-search-results-grid">';
      products.forEach(product => {
        const price = formatMoney(product.price);
        const resizedImgUrl = product.image ? getResizedImageUrl(product.image, '200x') : '';
        const imageHtml = resizedImgUrl 
          ? `<img src="${resizedImgUrl}" alt="${escapeHtml(product.title)}" loading="lazy" width="200" height="267">` 
          : `<div class="intense-placeholder">Fragrance</div>`;

        html += `
          <a href="${product.url}" class="intense-search-result-item" onclick="saveRecentSearch('${escapeHtml(product.title)}')">
            <div class="intense-search-result-img">
              ${imageHtml}
            </div>
            <h4 class="intense-search-result-title">${escapeHtml(product.title)}</h4>
            <div class="intense-search-result-price">${price}</div>
          </a>
        `;
      });
      html += '</div>';
      resultsContainer.innerHTML = html;
    })
    .catch(err => {
      console.error('Error fetching predictive search:', err);
      loader.style.display = 'none';
      if (resultsContainer) {
        resultsContainer.innerHTML = `<div class="intense-search-suggestions__no-results">Error loading recommendations. Please press enter to search.</div>`;
      }
    });
}

function saveRecentSearch(term) {
  let recents = JSON.parse(localStorage.getItem('intense_recent_searches')) || [];
  recents = recents.filter(item => item.toLowerCase() !== term.toLowerCase());
  recents.unshift(term);
  recents = recents.slice(0, 5); // Keep last 5 searches
  localStorage.setItem('intense_recent_searches', JSON.stringify(recents));
}

function renderRecentSearches() {
  const container = document.querySelector('.js-intense-recent-list');
  const parent = document.querySelector('.js-intense-search-recent');
  if (!container || !parent) return;

  const recents = JSON.parse(localStorage.getItem('intense_recent_searches')) || [];

  if (recents.length === 0) {
    parent.style.display = 'none';
    return;
  }

  parent.style.display = 'block';
  let html = '';
  recents.forEach(term => {
    html += `
      <li class="intense-search-list-item">
        <a href="#" class="intense-search-list-link js-trending-search-item" data-search-term="${escapeHtml(term)}">${escapeHtml(term)}</a>
      </li>
    `;
  });
  container.innerHTML = html;

  // Re-bind click events
  container.querySelectorAll('.js-trending-search-item').forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const term = item.getAttribute('data-search-term');
      const searchInput = document.querySelector('.js-intense-search-input');
      if (searchInput) {
        searchInput.value = term;
        searchInput.dispatchEvent(new Event('input'));
      }
    });
  });
}

/**
 * Dynamic Cart Drawer Logic
 */
function initCartDrawer() {
  // Listen for quantity changes
  const drawer = document.getElementById('intense-cart-drawer');
  if (!drawer) return;

  drawer.addEventListener('click', (e) => {
    if (e.target.classList.contains('js-qty-btn')) {
      const input = e.target.closest('.intense-cart-drawer__qty-control').querySelector('.js-qty-input');
      const key = e.target.getAttribute('data-key');
      let currentVal = parseInt(input.value);
      const isPlus = e.target.classList.contains('js-qty-plus');

      if (isPlus) {
        currentVal++;
      } else {
        if (currentVal > 1) currentVal--;
      }

      input.value = currentVal;
      updateCartItemQuantity(key, currentVal);
    }

    if (e.target.classList.contains('js-remove-item')) {
      e.preventDefault();
      const key = e.target.getAttribute('data-key');
      updateCartItemQuantity(key, 0);
    }

    // Add to cart recommendation
    if (e.target.classList.contains('js-rec-add-to-cart')) {
      const id = e.target.getAttribute('data-variant-id');
      addToCartFromRecs(id);
    }
  });

  // Hijack standard add-to-cart forms to open in drawer
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.action && form.action.includes('/cart/add')) {
      e.preventDefault();
      const formData = new FormData(form);
      
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(res => res.json())
      .then(item => {
        // Open cart drawer
        openDrawer('intense-cart-drawer');
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
        // Fallback to normal form submit if error
        form.submit();
      });
    }
  });
}

function refreshCartContent() {
  const container = document.querySelector('.js-cart-drawer-items');
  const footer = document.querySelector('.js-cart-drawer-footer');
  const shippingBar = document.querySelector('.js-shipping-bar-container');
  
  if (!container) return;

  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      // Update global header cart count
      updateCartCountBadge(cart.item_count);

      if (cart.item_count === 0) {
        container.innerHTML = `
          <div class="intense-cart-drawer__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25">
              <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <p>Your shopping bag is empty.</p>
            <a href="/collections/all" class="intense-btn intense-btn--primary js-drawer-close-btn">Continue Shopping</a>
          </div>
        `;
        if (footer) footer.style.display = 'none';
        if (shippingBar) shippingBar.style.display = 'none';
        
        // Add listener to the dynamically created continue shopping button
        const closeBtn = container.querySelector('.js-drawer-close-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeAllDrawers();
          });
        }
        return;
      }

      if (footer) footer.style.display = 'block';
      if (shippingBar) shippingBar.style.display = 'block';

      // Render shipping progress
      renderShippingProgress(cart);

      // Render items
      let html = '';
      cart.items.forEach(item => {
        const itemPrice = formatMoney(item.final_line_price);
        const optionsHtml = item.options_with_values 
          ? item.options_with_values.map(opt => `${opt.name}: ${opt.value}`).join(' / ') 
          : '';
        
        const imageUrl = item.image || (item.featured_image && item.featured_image.url) || '';
        const resizedImgUrl = imageUrl ? getResizedImageUrl(imageUrl, '200x') : '';
        const imageHtml = resizedImgUrl 
          ? `<img src="${resizedImgUrl}" alt="${escapeHtml(item.title)}">` 
          : `<div class="intense-placeholder">Fragrance</div>`;

        html += `
          <div class="intense-cart-drawer__item">
            <div class="intense-cart-drawer__item-img">
              ${imageHtml}
            </div>
            <div class="intense-cart-drawer__item-info">
              <div>
                <a href="${item.url}" class="intense-cart-drawer__item-title">${escapeHtml(item.product_title)}</a>
                <div class="intense-cart-drawer__item-variant">${escapeHtml(optionsHtml)}</div>
              </div>
              <div class="intense-cart-drawer__qty-control">
                <button type="button" class="intense-cart-drawer__qty-btn js-qty-btn js-qty-minus" data-key="${item.key}">-</button>
                <input class="intense-cart-drawer__qty-input js-qty-input" type="number" readonly value="${item.quantity}">
                <button type="button" class="intense-cart-drawer__qty-btn js-qty-btn js-qty-plus" data-key="${item.key}">+</button>
              </div>
            </div>
            <div class="intense-cart-drawer__item-right">
              <span class="intense-cart-drawer__item-price">${itemPrice}</span>
              <button type="button" class="intense-cart-drawer__item-remove js-remove-item" data-key="${item.key}">Remove</button>
            </div>
          </div>
        `;
      });
      container.innerHTML = html;

      // Render subtotal
      const subtotalEl = document.querySelector('.js-cart-drawer-subtotal');
      if (subtotalEl) {
        subtotalEl.innerText = formatMoney(cart.total_price);
      }
    })
    .catch(err => {
      console.error('Error refreshing cart drawer:', err);
    });
}

function updateCartItemQuantity(key, quantity) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      id: key,
      quantity: quantity
    })
  })
  .then(res => res.json())
  .then(cart => {
    refreshCartContent();
  })
  .catch(err => {
    console.error('Error updating cart item:', err);
  });
}

function addToCartFromRecs(variantId) {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  })
  .then(res => res.json())
  .then(() => {
    refreshCartContent();
  })
  .catch(err => {
    console.error('Error adding recommended item:', err);
  });
}

function renderShippingProgress(cart) {
  const container = document.querySelector('.js-shipping-bar-container');
  if (!container) return;

  const limitAttr = container.getAttribute('data-free-shipping-threshold');
  if (!limitAttr) return;

  const threshold = parseFloat(limitAttr) * 100; // Convert to cents
  const total = cart.items_subtotal_price;
  const fill = container.querySelector('.js-shipping-bar-fill');
  const msg = container.querySelector('.js-shipping-bar-message');

  if (total >= threshold) {
    fill.style.width = '100%';
    msg.innerHTML = 'Congratulations! You qualify for <strong>FREE SHIPPING</strong>.';
  } else {
    const percent = Math.min((total / threshold) * 100, 100);
    fill.style.width = `${percent}%`;
    const remaining = threshold - total;
    msg.innerHTML = `You are only <strong>${formatMoney(remaining)}</strong> away from <strong>FREE SHIPPING</strong>.`;
  }
}

function updateCartCountBadge(count) {
  const badge = document.querySelector('.intense-cart-count');
  if (badge) {
    badge.innerText = count;
    badge.style.display = count === 0 ? 'none' : 'flex';
  }
}

/**
 * Mobile Navigation Menu Drawer & Accordions
 */
function initMobileMenu() {
  const burger = document.querySelector('.js-intense-hamburger-toggle');
  const headerWrapper = document.querySelector('.intense-header-wrapper');
  
  if (!burger) return;

  burger.addEventListener('click', (e) => {
    e.preventDefault();
    if (headerWrapper.classList.contains('mobile-menu-open')) {
      closeAllDrawers();
    } else {
      closeAllDrawers();
      headerWrapper.classList.add('mobile-menu-open');
      document.body.style.overflow = 'hidden';
    }
  });

  // Accordion submenus
  document.querySelectorAll('.js-mobile-submenu-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const parent = btn.closest('.intense-mobile-menu__item');
      const submenu = parent.querySelector('.intense-mobile-submenu');

      if (parent.classList.contains('is-open')) {
        parent.classList.remove('is-open');
        slideUp(submenu, 300);
      } else {
        parent.classList.add('is-open');
        slideDown(submenu, 300);
      }
    });
  });
}

/**
 * Slide animations for accordions
 */
function slideUp(target, duration = 300) {
  target.style.transitionProperty = 'height, margin, padding';
  target.style.transitionDuration = duration + 'ms';
  target.style.boxSizing = 'border-box';
  target.style.height = target.offsetHeight + 'px';
  target.offsetHeight; // Force reflow
  target.style.overflow = 'hidden';
  target.style.height = '0';
  target.style.paddingTop = '0';
  target.style.paddingBottom = '0';
  target.style.marginTop = '0';
  target.style.marginBottom = '0';
  
  window.setTimeout(() => {
    target.style.display = 'none';
    target.style.removeProperty('height');
    target.style.removeProperty('padding-top');
    target.style.removeProperty('padding-bottom');
    target.style.removeProperty('margin-top');
    target.style.removeProperty('margin-bottom');
    target.style.removeProperty('overflow');
    target.style.removeProperty('transition-duration');
    target.style.removeProperty('transition-property');
  }, duration);
}

function slideDown(target, duration = 300) {
  target.style.removeProperty('display');
  let display = window.getComputedStyle(target).display;
  if (display === 'none') display = 'block';
  target.style.display = display;

  const height = target.offsetHeight;
  target.style.overflow = 'hidden';
  target.style.height = '0';
  target.style.paddingTop = '0';
  target.style.paddingBottom = '0';
  target.style.marginTop = '0';
  target.style.marginBottom = '0';
  target.offsetHeight; // Force reflow
  
  target.style.boxSizing = 'border-box';
  target.style.transitionProperty = 'height, margin, padding';
  target.style.transitionDuration = duration + 'ms';
  target.style.height = height + 'px';
  target.style.removeProperty('padding-top');
  target.style.removeProperty('padding-bottom');
  target.style.removeProperty('margin-top');
  target.style.removeProperty('margin-bottom');
  
  window.setTimeout(() => {
    target.style.removeProperty('height');
    target.style.removeProperty('overflow');
    target.style.removeProperty('transition-duration');
    target.style.removeProperty('transition-property');
  }, duration);
}

/**
 * Keyboard Navigation & Focus Trap (Accessibility)
 */
function initAccessibility() {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllDrawers();
    }
  });
}

/**
 * Helper Functions
 */
function getResizedImageUrl(url, size) {
  if (!url) return '';
  const matches = url.match(/(.*)\.(jpg|jpeg|gif|png|bmp|webp)(\?v=\d+)?$/i);
  if (matches) {
    return `${matches[1]}_${size}.${matches[2]}${matches[3] || ''}`;
  }
  return url;
}

function formatMoney(cents) {
  // Check if theme has Shopify.formatMoney, otherwise fallback
  if (window.Shopify && window.Shopify.formatMoney) {
    try {
      const format = (window.cartStrings && window.cartStrings.money_format) || undefined;
      return window.Shopify.formatMoney(cents, format);
    } catch (e) {
      console.warn("Shopify.formatMoney failed, using fallback formatter:", e);
    }
  }

  // Format currency fallback (usually USD)
  const dollars = (cents / 100).toFixed(2);
  // Read currency symbol from theme settings if possible, default to $
  const currencySymbol = window.themeCurrencySymbol || '$';
  return `${currencySymbol}${dollars}`;
}

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Programmatic Checkerboard Background Removal for Logo Image
 * Key-out white/gray checkered backgrounds from JPG images and output smooth transparent PNGs.
 */
function initLogoBackgroundRemoval() {
  const logoImgs = document.querySelectorAll('.intense-logo-img');
  logoImgs.forEach(img => {
    if (img.dataset.logoProcessed) return;

    const process = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i+1];
          const b = data[i+2];
          const brightness = (r + g + b) / 3;

          if (brightness > 120) {
            data[i+3] = 0; // Make transparent
          } else if (brightness < 40) {
            data[i] = 0;
            data[i+1] = 0;
            data[i+2] = 0;
            data[i+3] = 255; // Make solid black
          } else {
            // Smooth anti-aliased edge interpolation
            const factor = (brightness - 40) / 80;
            data[i] = 0;
            data[i+1] = 0;
            data[i+2] = 0;
            data[i+3] = Math.round(255 * (1 - factor));
          }
        }

        ctx.putImageData(imgData, 0, 0);
        img.dataset.logoProcessed = 'true';
        img.src = canvas.toDataURL('image/png');
      } catch (err) {
        console.error('Error removing logo background:', err);
      }
    };

    if (img.complete) {
      process();
    } else {
      img.addEventListener('load', process);
    }
  });
}

// Expose custom cart drawer functions globally for integration with other theme scripts
window.refreshCartContent = refreshCartContent;
window.openDrawer = openDrawer;
